import React, { useState, useEffect } from 'react';
import './VendorManagement.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const getAuthHeaders = (headers = {}) => {
  const token = localStorage.getItem('authToken');
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
};

const getVendorName = (vendor) => (
  vendor?.name || vendor?.vendorName || vendor?.competitorName || vendor?.sellerName || ''
);

const VendorManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    // Filtrer les vendeurs
    const normalizedSearch = searchTerm.toLowerCase();
    const filtered = vendors.filter(vendor =>
      getVendorName(vendor).toLowerCase().includes(normalizedSearch) ||
      String(vendor.email || '').toLowerCase().includes(normalizedSearch)
    );
    setFilteredVendors(filtered);
  }, [vendors, searchTerm]);

  const fetchVendors = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/competitors`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Erreur lors du chargement');
      const data = await response.json();
      setVendors(data.data || data || []);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `${API_BASE_URL}/competitors/${editingId}` : `${API_BASE_URL}/competitors`;

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || result.message || 'Erreur lors de la sauvegarde');
      }

      setFormData({ name: '', website: '', email: '', phone: '' });
      setEditingId(null);
      setShowForm(false);
      await fetchVendors();
    } catch (err) {
      setError(err.message);
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (vendor) => {
    setFormData({
      name: getVendorName(vendor),
      website: vendor.website || '',
      email: vendor.email || '',
      phone: vendor.phone || ''
    });
    setEditingId(vendor.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce vendeur ?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/competitors/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || result.message || 'Erreur lors de la suppression');
      }
      
      await fetchVendors();
    } catch (err) {
      setError(err.message);
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', website: '', email: '', phone: '' });
  };

  return (
    <div className="vendor-management">
      <h2>Gestion des Vendeurs</h2>

      {error && <div className="error-message">{error}</div>}

      {!showForm ? (
        <>
          <div className="search-bar">
            <input
              type="text"
              placeholder="Rechercher un vendeur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
              disabled={loading}
            >
              + Ajouter Vendeur
            </button>
          </div>

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : (
            <div className="vendors-list">
              {filteredVendors.length === 0 ? (
                <p className="no-data">Aucun vendeur trouvé</p>
              ) : (
                <table className="vendors-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Website</th>
                      <th>Email</th>
                      <th>Téléphone</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVendors.map((vendor, index) => (
                      <tr key={vendor.id || `${getVendorName(vendor)}-${vendor.email || vendor.website || index}`}>
                        <td>{getVendorName(vendor) || '-'}</td>
                        <td>
                          {vendor.website ? (
                            <a href={vendor.website} target="_blank" rel="noopener noreferrer">
                              {vendor.website}
                            </a>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{vendor.email || '-'}</td>
                        <td>{vendor.phone || '-'}</td>
                        <td>
                          <button
                            className="btn btn-edit"
                            onClick={() => handleEdit(vendor)}
                            disabled={loading}
                          >
                            Modifier
                          </button>
                          <button
                            className="btn btn-delete"
                            onClick={() => handleDelete(vendor.id)}
                            disabled={loading}
                          >
                            Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="form-container">
          <h3>{editingId ? 'Modifier Vendeur' : 'Ajouter Vendeur'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nom *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Nom du vendeur"
              />
            </div>

            <div className="form-group">
              <label>Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://example.com"
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="email@example.com"
              />
            </div>

            <div className="form-group">
              <label>Téléphone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+33 1 23 45 67 89"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-success" disabled={loading}>
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default VendorManagement;
